/**
 * CommunityInfoCard — Generalized two-line info card on the Community page.
 *
 * Renders at the top of the Alignment tab. Hidden when all fields are empty.
 *
 * Slot 1: locked MapPin icon + address.
 * Slot 2: admin-chosen icon (from a constrained palette) + freeform text.
 *
 * Use cases:
 *   - Rotary club: address + "Every Tuesday at 7:00 AM" (clock icon)
 *   - Foundation: address + "Open by appointment" (info icon)
 *   - Geographic community: town hall address + "Monthly meeting, first Saturday"
 *   - School: campus address + "Visiting hours" (calendar icon)
 *
 * DEV-968.
 */

import React from 'react';
import {
  MapPin, Edit2,
  Clock, Calendar, Info, Phone, Mail, Globe, Users, Building, Star,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import styles from './CommunityInfoCard.module.css';

interface Props {
  /** Slot 1 — physical address (free text). */
  address?: string | null;
  /** Slot 2 — Tabler-style icon key (no `ti-` prefix). One of the allowed palette values. */
  infoIconKey?: string | null;
  /** Slot 2 — freeform text paired with the chosen icon. */
  infoText?: string | null;
  /** Whether the current user can edit (drives the Edit affordance visibility). */
  canEdit?: boolean;
  /** Open the edit surface (community settings, info-card section). */
  onEdit?: () => void;
}

/**
 * Map a stored icon key to a lucide-react component. Keys mirror the DB
 * CHECK constraint in migration 145 and `AllowedInfoIconKeys` in
 * CommunityMetadataController.cs.
 */
const ICON_BY_KEY: Record<string, LucideIcon> = {
  clock: Clock,
  'calendar-event': Calendar,
  'info-circle': Info,
  phone: Phone,
  mail: Mail,
  world: Globe,
  users: Users,
  building: Building,
  star: Star,
};

const CommunityInfoCard: React.FC<Props> = ({ address, infoIconKey, infoText, canEdit, onEdit }) => {
  const { t } = useAppTranslation('community');

  const hasAddress = !!(address && address.trim().length > 0);
  const hasInfo = !!(infoText && infoText.trim().length > 0);

  // Card hidden when there's nothing to show AND the viewer can't edit.
  // Admins always see at least an empty placeholder + Edit affordance so
  // they have a clear entry point to populate the card.
  if (!hasAddress && !hasInfo && !canEdit) return null;

  const SecondaryIcon = (infoIconKey && ICON_BY_KEY[infoIconKey]) || null;

  return (
    <div className={styles.card}>
      <div className={styles.rows}>
        {hasAddress && (
          <div className={styles.row}>
            <MapPin size={18} strokeWidth={1.75} className={styles.icon} aria-hidden="true" />
            <span className={styles.text}>{address}</span>
          </div>
        )}
        {hasInfo && (
          <div className={styles.row}>
            {SecondaryIcon ? (
              <SecondaryIcon size={18} strokeWidth={1.75} className={styles.icon} aria-hidden="true" />
            ) : (
              <Info size={18} strokeWidth={1.75} className={styles.icon} aria-hidden="true" />
            )}
            <span className={styles.text}>{infoText}</span>
          </div>
        )}
        {!hasAddress && !hasInfo && canEdit && (
          <div className={styles.empty}>{t('infoCard.emptyHintAdmin')}</div>
        )}
      </div>

      {canEdit && onEdit && (
        <button
          type="button"
          className={styles.editBtn}
          onClick={onEdit}
          aria-label={t('infoCard.editAriaLabel')}
        >
          <Edit2 size={13} strokeWidth={2} aria-hidden="true" />
          <span>{t('infoCard.edit')}</span>
        </button>
      )}
    </div>
  );
};

export default CommunityInfoCard;
